import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import productImg from "../assets/productImg3.png";

gsap.registerPlugin(ScrollTrigger);

const features = [
  {
    tag: "01 — Portabilitet",
    heading: "Hyn në\nçantën tënde.\nGjithmonë.",
    body: "Vetëm 180g — Siara Styler zhduket në çdo çantë. Çantë dore, çantë sporti, xhep pallto. Shkon kudo që shkon ti.",
    side: "left",
  },
  {
    tag: "02 — Energji",
    heading: "USB-C.\nPa nevojë\npër prizë.",
    body: "Një mbushje zgjat gjithë ditën. Lidhe me power bank, laptop apo makinë — dhe dil gjithmonë e bukur.",
    side: "right",
  },
  {
    tag: "03 — Siguri",
    heading: "Flokët tu\nmeritojnë\nmë të mirën.",
    body: "Shpërndarja jonike e nxehtësisë eliminon djegiet dhe dëmtimet. Vetëm flokë të lëmuar, të shëndetshëm — çdo herë.",
    side: "left",
  },
  {
    tag: "04 — Rezultate",
    heading: "Cilësi salloni.\nÇdo mëngjes.\nKudo.",
    body: "Dizajni me qime fleksibël punon me flokët tuaj, jo kundër tyre. Të drejtë, me volum dhe të lëmuar — brenda 3 minutave.",
    side: "right",
  },
];

const ScrollFeatures = () => {
  const sectionRef = useRef(null);
  const textRefs = useRef([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      textRefs.current.forEach((el, i) => {
        const fromLeft = features[i].side === "left";

        gsap.fromTo(
          el,
          { opacity: 0, x: fromLeft ? -50 : 50 },
          {
            opacity: 1,
            x: 0,
            ease: "power2.out",
            scrollTrigger: {
              trigger: el,
              start: "top 72%",
              end: "top 38%",
              scrollTrigger: sectionRef.current,
              scrub: 0.8,
            },
          },
        );
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <Section ref={sectionRef} id="features">
      <StickyProduct>
        <img src={productImg} alt="Siara Styler" draggable="false" />
        <Glow />
      </StickyProduct>

      <Panels>
        {features.map((f, i) => (
          <Panel key={i} $side={f.side}>
            <FeatureText
              ref={(el) => (textRefs.current[i] = el)}
              $side={f.side}
            >
              <Tag>{f.tag}</Tag>
              <Heading>
                {f.heading.split("\n").map((line, j) => (
                  <span key={j}>
                    {line}
                    <br />
                  </span>
                ))}
              </Heading>
              <Body>{f.body}</Body>
              <CTA href="/checkout">Porosit Tani →</CTA>
            </FeatureText>
          </Panel>
        ))}
      </Panels>
    </Section>
  );
};

const Section = styled.section`
  position: relative;
  background: linear-gradient(180deg, #fdf8f5 0%, #fceee8 100%);
`;

const StickyProduct = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 1;
  margin-bottom: -100vh;

  img {
    padding: 3em;
    width: 560px;
    object-fit: contain;
    filter: drop-shadow(0 24px 48px rgba(180, 120, 100, 0.28));
    position: relative;
    z-index: 1;
  }

  @media (max-width: 768px) {
    img {
      width: 90%;
    }
  }
`;

const Glow = styled.div`
  position: absolute;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(232, 197, 184, 0.5) 0%,
    transparent 70%
  );
  pointer-events: none;
`;

const Panels = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1100px;
  margin: 0 auto;
`;

const Panel = styled.div`
  min-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: ${({ $side }) =>
    $side === "left" ? "flex-start" : "flex-end"};
  padding: ${({ $side }) => ($side === "left" ? "0 0 0 -5rem" : "0 -3rem 0 0")};

  @media (max-width: 1200px) {
    padding: ${({ $side }) => ($side === "left" ? "0 0 0 4rem" : "0 4rem 0 0")};
  }

  @media (max-width: 900px) {
    justify-content: center;
    padding: 4rem 1.5rem;
    min-height: 70vh;
  }
`;

const FeatureText = styled.div`
  max-width: 300px;

  @media (max-width: 900px) {
    text-align: center;
    background: rgba(253, 248, 245, 0.85);
    backdrop-filter: blur(8px);
    border-radius: 16px;
    padding: 1.5rem;
  }
`;

const Tag = styled.p`
  font-family: var(--sans);
  font-size: 0.67rem;
  font-weight: 400;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--gold);
  margin-bottom: 0.8rem;
`;

const Heading = styled.h2`
  font-family: var(--serif);
  font-size: clamp(2rem, 3.5vw, 3.2rem);
  font-weight: 300;
  line-height: 1.15;
  color: var(--dark);
  margin-bottom: 0.9rem;
`;

const Body = styled.p`
  font-family: var(--sans);
  font-size: 0.88rem;
  font-weight: 300;
  line-height: 1.75;
  color: var(--mid);
  margin-bottom: 1.4rem;
  max-width: 280px;
`;

const CTA = styled.a`
  font-family: var(--sans);
  font-size: 0.73rem;
  font-weight: 500;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--dark);
  border-bottom: 1px solid var(--rose);
  padding-bottom: 3px;
  transition:
    border-color 0.3s ease,
    color 0.3s ease;

  &:hover {
    color: var(--gold);
    border-color: var(--gold);
  }
`;

export default ScrollFeatures;
